import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import Breadcrumbs from '../../components/ui/breadcrumbs';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import { Mail, Trash2, Download, Send, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterData, setNewsletterData] = useState({
    subject: '',
    content: '',
    template: 'default'
  });

  useEffect(() => {
    fetchSubscribers();
    fetchStats();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get('/admin/newsletters/subscriptions');

      // Handle different response formats
      let subscribersData = [];
      if (response.data?.subscriptions?.data) {
        // Paginated response
        subscribersData = response.data.subscriptions.data;
      } else if (response.data?.subscriptions) {
        // Direct array response
        subscribersData = response.data.subscriptions;
      } else if (response.data?.data) {
        // Alternative structure
        subscribersData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Direct array
        subscribersData = response.data;
      }

      setSubscribers(Array.isArray(subscribersData) ? subscribersData : []);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
      toast.error('Failed to fetch subscribers');
      setSubscribers([]); // Ensure subscribers is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/newsletters/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch newsletter stats');
    }
  };

  const toggleSubscriberStatus = async (id) => {
    try {
      await axios.post(`/admin/newsletters/subscriptions/${id}/toggle-status`);
      toast.success('Subscriber status updated');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to update subscriber status');
    }
  };

  const deleteSubscriber = async (id) => {
    if (window.confirm('Are you sure you want to delete this subscriber?')) {
      try {
        await axios.delete(`/admin/newsletters/subscriptions/${id}`);
        toast.success('Subscriber deleted successfully');
        fetchSubscribers();
      } catch (error) {
        toast.error('Failed to delete subscriber');
      }
    }
  };

  const exportSubscribers = async () => {
    try {
      const response = await axios.get('/admin/newsletters/export');
      const blob = new Blob([JSON.stringify(response.data.subscribers, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'newsletter-subscribers.json';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Subscribers exported successfully');
    } catch (error) {
      toast.error('Failed to export subscribers');
    }
  };

  const sendNewsletter = async (e) => {
    e.preventDefault();
    setSendingNewsletter(true);
    try {
      await axios.post('/admin/newsletters/send', newsletterData);
      toast.success('Newsletter sent successfully');
      setShowSendForm(false);
      setNewsletterData({
        subject: '',
        content: '',
        template: 'default'
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send newsletter');
    } finally {
      setSendingNewsletter(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading newsletter data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6"><Breadcrumbs /></div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Newsletter Management</h1>
        <div className="flex gap-2">
          <Button
            onClick={exportSubscribers}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button
            onClick={() => setShowSendForm(true)}
            className="flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            Send Newsletter
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-blue-600">{stats.total_subscribers || 0}</div>
            <p className="text-gray-600">Total Subscribers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-green-600">{stats.active_subscribers || 0}</div>
            <p className="text-gray-600">Active Subscribers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-yellow-600">{stats.this_month || 0}</div>
            <p className="text-gray-600">This Month</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-purple-600">{stats.newsletters_sent || 0}</div>
            <p className="text-gray-600">Newsletters Sent</p>
          </CardContent>
        </Card>
      </div>

      {/* Send Newsletter Form */}
      {showSendForm && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Send Newsletter</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={sendNewsletter} className="space-y-4">
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={newsletterData.subject}
                  onChange={(e) => setNewsletterData({...newsletterData, subject: e.target.value})}
                  placeholder="Newsletter subject..."
                  required
                />
              </div>

              <div>
                <Label htmlFor="content">Content</Label>
                <textarea
                  id="content"
                  value={newsletterData.content}
                  onChange={(e) => setNewsletterData({...newsletterData, content: e.target.value})}
                  className="w-full h-64 p-3 border rounded-md"
                  placeholder="Newsletter content (HTML supported)..."
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={sendingNewsletter}>
                  {sendingNewsletter ? 'Sending...' : 'Send Newsletter'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSendForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Subscribers List */}
      <Card>
        <CardHeader>
          <CardTitle>Subscribers ({subscribers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {subscribers.map((subscriber) => (
              <div key={subscriber.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="font-medium">{subscriber.email}</div>
                    <div className="text-sm text-gray-500">
                      {subscriber.name && `${subscriber.name} • `}
                      Subscribed {new Date(subscriber.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    subscriber.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {subscriber.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleSubscriberStatus(subscriber.id)}
                  >
                    {subscriber.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteSubscriber(subscriber.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {subscribers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No subscribers found
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNewsletter;
